import { Disclosure } from '@headlessui/react'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import clsx from 'clsx'

import { Icon } from 'ui'

import type { Cart, CartItem, CartItemStyle } from 'types/graphql/website/__generated__/graphql'

import { formatCost } from 'utils'
import CartItemStyleSummary from '@/components/cart/cartSummary/CartItemStyleSummary'
import { ProductsAddOn } from '@/lib/types/products'
import { useMemo } from 'react'

type Props = {
	cart: Cart
	item: CartItem & {
		totalPrice: number
		shippingPrice: number
		subtotalPrice: number
		taxPrice: number
		addOns: ProductsAddOn[]
	}
}

export default function CartItemSummary({ cart, item }: Readonly<Props>) {
	// Calculate rush fee
	const rushFee = useMemo(() => {
		return (
			item.totalPrice +
			item.shippingPrice +
			item.taxPrice -
			item.subtotalPrice -
			item.shippingPrice -
			item.taxPrice
		)
	}, [item.totalPrice, item.shippingPrice, item.taxPrice, item.subtotalPrice])

	return (
		<Disclosure as="div" className="w-full">
			{({ open }) => (
				<>
					<Disclosure.Button className="w-full">
						<div className={clsx('rounded-lg', 'flex w-full space-x-4')}>
							<div className="w-full flex justify-between text-lg">
								<div className="flex gap-2 text-left items-center">
									<span className="font-sans font-medium">{item.name}</span>

									<Icon
										className="text-xs text-gray-400 mb-1.5"
										icon={open ? faChevronUp : faChevronDown}
									/>
								</div>
								<div>
									<span className="text-start font-sans font-medium text-black-rt">
										{formatCost(item.totalPrice + item.shippingPrice + item.taxPrice)}
									</span>
								</div>
							</div>
						</div>
					</Disclosure.Button>
					<Disclosure.Panel className="w-full text-[13px] font-thin">
						<div className="w-full flex flex-col gap-2 mt-2">
							<div className="flex flex-col gap-2 w-full">
								{item.itemStyles?.map(
									(
										itemStyle: CartItemStyle & {
											totalPrice: number
										}
									) => (
										<CartItemStyleSummary style={itemStyle} key={itemStyle.token} />
									)
								)}
							</div>

							{item.addOns?.length > 0 && (
								<div className="flex flex-col gap-2">
									{item.addOns?.map((addOn) => (
										<div className="flex justify-between" key={addOn.key}>
											<span>{addOn.title}</span>
											<span>{formatCost(addOn.totalPrice)}</span>
										</div>
									))}
								</div>
							)}

							<div className="flex justify-between">
								<span>Subtotal</span>
								<span>{formatCost(item.subtotalPrice)}</span>
							</div>

							<div className="flex justify-between">
								<span>Shipping</span>
								<span>
									{formatCost(item.shippingPrice) === '$0.00'
										? 'Free shipping'
										: formatCost(item.shippingPrice)}
								</span>
							</div>

							{rushFee > 0 && (
								<div className="flex justify-between">
									<span>Expedited turnaround</span>
									<span>{formatCost(rushFee)}</span>
								</div>
							)}

							<div className="flex justify-between">
								<span>Tax</span>
								<span>{cart?.state ? formatCost(item.taxPrice) : 'TBD'}</span>
							</div>
						</div>
					</Disclosure.Panel>
				</>
			)}
		</Disclosure>
	)
}
